import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FaStore, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaCheckCircle, FaPercent, FaFire, FaStar } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';

const categoriaVacia = { label: '', value: '', descripcion: '', order: 0 };

function formatDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * Gestión de REDPLIKAPLACE (admin):
 * - Categorías del marketplace (crear nuevas sin desarrollos)
 * - Vendedores: % de comisión Redplika por acuerdo y confirmación de cuenta ePayco
 */
const GestionMarketplace = () => {
  const [tab, setTab] = useState('categorias'); // categorias | vendedores
  const [loading, setLoading] = useState(true);

  // Categorías
  const [categorias, setCategorias] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(categoriaVacia);
  const [error, setError] = useState('');

  // Vendedores
  const [vendedores, setVendedores] = useState([]);
  const [defaultSplit, setDefaultSplit] = useState(30);

  // Publicaciones (destacados)
  const [publicaciones, setPublicaciones] = useState([]);

  const fetchCategorias = async () => {
    try {
      const res = await apiClient.get('/api/marketplace/gestion/categorias');
      setCategorias(Array.isArray(res.data) ? res.data : []);
    } catch {
      Swal.fire('Error', 'No se pudieron cargar las categorías.', 'error');
    }
  };

  const fetchVendedores = async () => {
    try {
      const res = await apiClient.get('/api/marketplace/gestion/vendedores');
      setVendedores(res.data?.vendedores || []);
      setDefaultSplit(res.data?.defaultSplitPercent ?? 30);
    } catch {
      Swal.fire('Error', 'No se pudieron cargar los vendedores.', 'error');
    }
  };

  const fetchPublicaciones = async () => {
    try {
      const res = await apiClient.get('/api/marketplace/gestion/publicaciones');
      setPublicaciones(Array.isArray(res.data) ? res.data : []);
    } catch {
      Swal.fire('Error', 'No se pudieron cargar las publicaciones.', 'error');
    }
  };

  useEffect(() => {
    Promise.all([fetchCategorias(), fetchVendedores(), fetchPublicaciones()]).finally(() => setLoading(false));
  }, []);

  const toggleDestacado = async (pub) => {
    try {
      const res = await apiClient.patch(`/api/marketplace/gestion/publicaciones/${pub._id}/destacado`, {
        destacado: !pub.destacado,
      });
      setPublicaciones(prev => prev.map(p => (p._id === pub._id ? { ...p, destacado: res.data.destacado } : p)));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'No se pudo actualizar la publicación', 'error');
    }
  };

  /* ============ Categorías ============ */

  const openCreate = () => {
    setEditItem(null);
    setForm(categoriaVacia);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      label: item.label,
      value: item.value,
      descripcion: item.descripcion || '',
      order: item.order ?? 0,
    });
    setError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      setError('El nombre de la categoría es obligatorio');
      return;
    }
    const payload = {
      label: form.label.trim(),
      descripcion: form.descripcion.trim(),
      order: Number(form.order) || 0,
      value: form.value.trim() || form.label.trim(),
    };
    try {
      if (editItem) {
        const res = await apiClient.put(`/api/marketplace/gestion/categorias/${editItem._id}`, payload);
        setCategorias(prev => prev.map(c => (c._id === editItem._id ? res.data : c)));
        Swal.fire('Actualizada', 'La categoría fue actualizada.', 'success');
      } else {
        const res = await apiClient.post('/api/marketplace/gestion/categorias', payload);
        setCategorias(prev => [...prev, res.data].sort((a, b) => (a.order || 0) - (b.order || 0)));
        Swal.fire('Creada', 'La categoría ya está disponible en REDPLIKAPLACE.', 'success');
      }
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar la categoría');
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await apiClient.patch(`/api/marketplace/gestion/categorias/${item._id}/estado`, {
        active: !item.active,
      });
      setCategorias(prev => prev.map(c => (c._id === item._id ? res.data : c)));
    } catch (err) {
      Swal.fire('No permitido', err.response?.data?.message || 'No se pudo cambiar el estado', 'warning');
    }
  };

  const deleteCategoria = async (item) => {
    const result = await Swal.fire({
      title: '¿Eliminar categoría?',
      text: `Se eliminará "${item.label}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await apiClient.delete(`/api/marketplace/gestion/categorias/${item._id}`);
      setCategorias(prev => prev.filter(c => c._id !== item._id));
      Swal.fire('Eliminada', 'La categoría fue eliminada.', 'success');
    } catch (err) {
      Swal.fire('No permitido', err.response?.data?.message || 'No se pudo eliminar', 'warning');
    }
  };

  /* ============ Vendedores ============ */

  const editarSplit = async (vendedor) => {
    const { value: nuevoSplit, isConfirmed } = await Swal.fire({
      title: `Comisión Redplika para ${vendedor.fullName}`,
      input: 'number',
      inputLabel: `% de comisión sobre cada venta (vacío = usar default global de ${defaultSplit}%)`,
      inputValue: vendedor.marketplaceSplitPercent ?? '',
      inputAttributes: { min: 0, max: 100, step: 1 },
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      await apiClient.put(`/api/marketplace/gestion/vendedores/${vendedor._id}/split`, {
        splitPercent: nuevoSplit === '' ? null : Number(nuevoSplit),
      });
      await fetchVendedores();
      Swal.fire('Guardado', 'La comisión fue actualizada.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'No se pudo actualizar la comisión', 'error');
    }
  };

  const confirmarEpayco = async (vendedor) => {
    const { value: epaycoId, isConfirmed } = await Swal.fire({
      title: `Confirmar cuenta ePayco de ${vendedor.fullName}`,
      input: 'text',
      inputLabel: 'ID de la cuenta ePayco del vendedor (confirma que está matriculado en la app de split)',
      inputValue: vendedor.epaycoAliadoId || '',
      showCancelButton: true,
      confirmButtonText: 'Confirmar cuenta',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || !v.trim() ? 'El ID de ePayco es requerido' : undefined),
    });
    if (!isConfirmed) return;
    try {
      await apiClient.put(`/api/marketplace/gestion/vendedores/${vendedor._id}/confirmar-epayco`, {
        epaycoAliadoId: epaycoId.trim(),
      });
      await fetchVendedores();
      Swal.fire('Confirmada', 'El vendedor ya puede publicar y recibir pagos.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'No se pudo confirmar la cuenta', 'error');
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-slate-500">Cargando gestión de REDPLIKAPLACE...</div>;
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-3 mb-6">
        <div className="grid place-items-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#1f94c2] to-[#205087] text-white shadow-lg">
          <FaStore className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Gestión REDPLIKAPLACE</h1>
          <p className="text-slate-500 text-sm">
            Categorías del marketplace y acuerdos de comisión con los vendedores.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-5">
        <button
          type="button"
          onClick={() => setTab('categorias')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'categorias' ? 'bg-[#205087] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1f94c2]'}`}
        >
          Categorías ({categorias.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('vendedores')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'vendedores' ? 'bg-[#205087] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1f94c2]'}`}
        >
          Vendedores ({vendedores.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('publicaciones')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === 'publicaciones' ? 'bg-[#205087] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-[#1f94c2]'}`}
        >
          Publicaciones ({publicaciones.length})
        </button>
      </div>

      {tab === 'categorias' && (
        <>
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-[#f79865] hover:bg-[#e88a55] transition-colors"
            >
              <FaPlus className="w-4 h-4" /> Nueva categoría
            </button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">Orden</th>
                  <th className="px-4 py-3 font-semibold">Nombre</th>
                  <th className="px-4 py-3 font-semibold">Identificador</th>
                  <th className="px-4 py-3 font-semibold">Descripción</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold">Última edición</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {categorias.map((cat) => (
                  <tr key={cat._id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-500">{cat.order ?? 0}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{cat.label}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{cat.value}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-[240px] truncate">{cat.descripcion || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cat.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {cat.active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(cat.ultimaEdicion)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" title={cat.active ? 'Desactivar' : 'Activar'} onClick={() => toggleActive(cat)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
                          {cat.active ? <FaToggleOn className="w-5 h-5 text-green-500" /> : <FaToggleOff className="w-5 h-5" />}
                        </button>
                        <button type="button" title="Editar" onClick={() => openEdit(cat)} className="p-2 rounded-lg text-[#205087] hover:bg-blue-50">
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button type="button" title="Eliminar" onClick={() => deleteCategoria(cat)} className="p-2 rounded-lg text-red-500 hover:bg-red-50">
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'vendedores' && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-[#205087]">
            Comisión global por defecto: <strong>{defaultSplit}%</strong> para Redplika por cada venta.
            Puedes definir un % distinto por vendedor según el acuerdo comercial.
          </div>
          {vendedores.length === 0 ? (
            <div className="text-center py-14 bg-white rounded-2xl border border-slate-200 text-slate-500">
              Aún no hay vendedores (publicaciones, ePayco pendiente o cuentas de marketplace confirmadas).
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-600">
                    <th className="px-4 py-3 font-semibold">Profesional</th>
                    <th className="px-4 py-3 font-semibold">Perfil</th>
                    <th className="px-4 py-3 font-semibold">Publicaciones</th>
                    <th className="px-4 py-3 font-semibold">Cuenta ePayco</th>
                    <th className="px-4 py-3 font-semibold">Comisión</th>
                    <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {vendedores.map((v) => (
                    <tr key={v._id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{v.fullName}</p>
                        <p className="text-xs text-slate-400">{v.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {v.tipoPerfil === 'experto' || v.prestacionServicio === false ? (
                          <span className="inline-flex flex-col text-xs font-semibold px-2.5 py-1 rounded-lg bg-orange-100 text-orange-800">
                            Experto
                            <span className="font-medium opacity-80 normal-case">Marketplace</span>
                          </span>
                        ) : v.tipoPerfil === 'asistencial' ? (
                          <span className="inline-flex flex-col text-xs font-semibold px-2.5 py-1 rounded-lg bg-sky-100 text-sky-800">
                            Asistencial
                            <span className="font-medium opacity-80 normal-case">Citas clínicas</span>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {v.publicadas}/{v.publicaciones} publicadas
                      </td>
                      <td className="px-4 py-3">
                        {v.habilitado ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                            <FaCheckCircle className="w-3 h-3" /> Confirmada
                          </span>
                        ) : v.epaycoAliadoId ? (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                            Pendiente confirmación
                          </span>
                        ) : (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                            Sin cuenta
                          </span>
                        )}
                        {v.epaycoAliadoId && (
                          <p className="text-[11px] text-slate-400 font-mono mt-1">{v.epaycoAliadoId}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-[#205087]">{v.splitAplicado}%</span>
                        {v.marketplaceSplitPercent === null || v.marketplaceSplitPercent === undefined ? (
                          <span className="text-xs text-slate-400 ml-1">(default)</span>
                        ) : (
                          <span className="text-xs text-slate-400 ml-1">(acuerdo)</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            title="Editar comisión"
                            onClick={() => editarSplit(v)}
                            className="p-2 rounded-lg text-[#205087] hover:bg-blue-50"
                          >
                            <FaPercent className="w-4 h-4" />
                          </button>
                          {!v.habilitado && (
                            <button
                              type="button"
                              title="Confirmar cuenta ePayco"
                              onClick={() => confirmarEpayco(v)}
                              className="p-2 rounded-lg text-green-600 hover:bg-green-50"
                            >
                              <FaCheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'publicaciones' && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-800">
            Las publicaciones <strong>destacadas</strong> aparecen primero en el catálogo y en la sección
            "Recomendados" del home de REDPLIKAPLACE.
          </div>
          {publicaciones.length === 0 ? (
            <div className="text-center py-14 bg-white rounded-2xl border border-slate-200 text-slate-500">
              Aún no hay publicaciones en el marketplace.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-slate-600">
                    <th className="px-4 py-3 font-semibold">Producto</th>
                    <th className="px-4 py-3 font-semibold">Vendedor</th>
                    <th className="px-4 py-3 font-semibold">Categoría</th>
                    <th className="px-4 py-3 font-semibold">Estado</th>
                    <th className="px-4 py-3 font-semibold text-right">Precio</th>
                    <th className="px-4 py-3 font-semibold">Rating</th>
                    <th className="px-4 py-3 font-semibold text-right">Ventas</th>
                    <th className="px-4 py-3 font-semibold text-center">Destacado</th>
                  </tr>
                </thead>
                <tbody>
                  {publicaciones.map((pub) => (
                    <tr key={pub._id} className="border-t border-slate-100">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800 flex items-center gap-1.5">
                          {pub.destacado && <FaFire className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                          {pub.titulo}
                        </p>
                        <p className="text-[11px] text-slate-400">{formatDate(pub.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-700">{pub.vendedor?.fullName || '—'}</p>
                        <p className="text-[11px] text-slate-400">{pub.vendedor?.email || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{pub.categoria}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pub.estado === 'publicado' ? 'bg-green-100 text-green-700' : pub.estado === 'pausado' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                          {pub.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {pub.descuentoPorcentaje > 0 ? (
                          <>
                            <span className="text-xs text-slate-400 line-through mr-1">
                              ${Number(pub.precio).toLocaleString('es-CO')}
                            </span>
                            <span className="font-semibold text-slate-700">
                              ${Number(pub.precioFinal).toLocaleString('es-CO')}
                            </span>
                            <span className="text-[11px] text-red-500 ml-1">-{pub.descuentoPorcentaje}%</span>
                          </>
                        ) : (
                          <span className="font-semibold text-slate-700">
                            ${Number(pub.precio).toLocaleString('es-CO')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {pub.ratingCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <FaStar className="w-3.5 h-3.5 text-amber-400" />
                            {Number(pub.ratingPromedio).toFixed(1)}
                            <span className="text-[11px] text-slate-400">({pub.ratingCount})</span>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">Sin valorar</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">{pub.ventas}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          title={pub.destacado ? 'Quitar destacado' : 'Destacar en el home'}
                          onClick={() => toggleDestacado(pub)}
                          className="p-2 rounded-lg hover:bg-slate-100"
                        >
                          {pub.destacado
                            ? <FaToggleOn className="w-6 h-6 text-amber-500" />
                            : <FaToggleOff className="w-6 h-6 text-slate-300" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal categoría */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <form onSubmit={handleSubmit} className="p-6">
              <h2 className="text-lg font-bold text-slate-800 mb-5">
                {editItem ? 'Editar categoría' : 'Nueva categoría'}
              </h2>
              {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Nombre visible *</label>
                  <input
                    type="text"
                    value={form.label}
                    onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                    placeholder="Ej: E-books"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Identificador interno (opcional)</label>
                  <input
                    type="text"
                    value={form.value}
                    onChange={(e) => setForm(f => ({ ...f, value: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2] font-mono"
                    placeholder="Se genera del nombre si se deja vacío"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Descripción</label>
                  <input
                    type="text"
                    value={form.descripcion}
                    onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1">Orden</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm(f => ({ ...f, order: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1f94c2]"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2 rounded-lg font-semibold text-slate-600 border border-slate-300 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-lg font-semibold text-white bg-[#205087] hover:bg-[#1f94c2]"
                >
                  {editItem ? 'Guardar cambios' : 'Crear categoría'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionMarketplace;
